import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const BRAND = '#fd9e10';

const SECTIONS = [
  { id: 'intro', title: 'Introduction' },
  { id: 'information-we-collect', title: 'Information We Collect' },
  { id: 'how-we-use-it', title: 'How We Use Your Information' },
  { id: 'sharing', title: 'Sharing With Vendors & Partners' },
  { id: 'cookies', title: 'Cookies & Tracking' },
  { id: 'security', title: 'Data Security' },
  { id: 'your-rights', title: 'Your Rights & Choices' },
  { id: 'children', title: "Children's Privacy" },
  { id: 'changes', title: 'Changes to This Policy' },
  { id: 'contact', title: 'Contact Us' },
];

export default function PrivacyPolicyPage() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );

    SECTIONS.forEach((s) => {
      const el = sectionRefs.current[s.id];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  function scrollTo(id: string) {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#fff8ee] via-[#fffaf3] to-white flex flex-col relative overflow-hidden">
      <style>{`
        @keyframes dz-float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(20px, -30px) scale(1.08); }
        }
        @keyframes dz-fade-up {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .dz-blob { animation: dz-float 12s ease-in-out infinite; }
        .dz-fade-up { opacity: 0; animation: dz-fade-up 0.6s ease-out forwards; }
        .dz-toc-link {
          position: relative;
          transition: color 0.2s ease, padding-left 0.2s ease;
        }
        .dz-toc-link.active {
          color: #c9660a;
          font-weight: 600;
          padding-left: 4px;
        }
        .dz-toc-link.active::before {
          content: '';
          position: absolute;
          left: -12px;
          top: 50%;
          transform: translateY(-50%);
          width: 3px;
          height: 16px;
          border-radius: 999px;
          background-image: linear-gradient(to bottom, #ffb74d, ${BRAND});
        }
      `}</style>

      <div className="dz-blob pointer-events-none absolute -top-32 -left-32 w-[26rem] h-[26rem] rounded-full bg-gradient-to-br from-[#fd9e10]/25 via-[#ffb74d]/15 to-transparent blur-3xl" />
      <div className="dz-blob pointer-events-none absolute top-1/2 -right-40 w-[28rem] h-[28rem] rounded-full bg-gradient-to-bl from-[#fd9e10]/20 to-transparent blur-3xl" style={{ animationDelay: '2s' }} />

      <main className="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 relative z-10">
        <nav className="text-xs text-gray-500 mb-4 dz-fade-up">
          <Link to="/" className="hover:text-gray-700 transition-colors">
            Home
          </Link>{' '}
          <span className="mx-1 text-gray-300">/</span>{' '}
          <span
            className="font-medium bg-clip-text text-transparent"
            style={{ backgroundImage: `linear-gradient(to right, ${BRAND}, #e8720c)` }}
          >
            Privacy Policy
          </span>
        </nav>

        <div className="dz-fade-up mb-10" style={{ animationDelay: '60ms' }}>
          <span
            className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-xs font-semibold tracking-wide border shadow-sm mb-4"
            style={{ color: '#c9660a', borderColor: '#ffd9a0' }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: BRAND }} />
            LEGAL
          </span>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900 mb-2">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500">Last updated: July 2026</p>
        </div>

        <div className="grid lg:grid-cols-[220px_1fr] gap-10">
          <aside className="hidden lg:block dz-fade-up" style={{ animationDelay: '120ms' }}>
            <div className="sticky top-8 space-y-1 pl-3 border-l border-gray-200">
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className={`dz-toc-link block text-left text-sm py-1.5 text-gray-500 hover:text-gray-800 w-full ${
                    activeId === s.id ? 'active' : ''
                  }`}
                >
                  {s.title}
                </button>
              ))}
            </div>
          </aside>

          <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6 sm:p-10 space-y-12 dz-fade-up" style={{ animationDelay: '160ms' }}>
            <section
              id="intro"
              ref={(el) => (sectionRefs.current['intro'] = el)}
              className="space-y-3 scroll-mt-8"
            >
              <h2 className="text-xl font-bold text-gray-900">1. Introduction</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                Dehleze ("we", "us", "our") operates a marketplace platform connecting
                vendors and customers. This Privacy Policy explains what information we
                collect when you use our website and services, how we use it, and the
                choices you have. By using Dehleze, you agree to the practices described
                here.
              </p>
            </section>

            <section
              id="information-we-collect"
              ref={(el) => (sectionRefs.current['information-we-collect'] = el)}
              className="space-y-3 scroll-mt-8"
            >
              <h2 className="text-xl font-bold text-gray-900">2. Information We Collect</h2>
              <p className="text-sm text-gray-600 leading-relaxed">We collect the following types of information:</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: BRAND }} />
                  <span><span className="font-semibold text-gray-800">Account information</span> — name, email address, phone number, and password when you register.</span>
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: BRAND }} />
                  <span><span className="font-semibold text-gray-800">Order information</span> — items purchased, delivery address, and payment status (we do not store full payment card details).</span>
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: BRAND }} />
                  <span><span className="font-semibold text-gray-800">Vendor information</span> — business details, product listings, and contact information if you register as a vendor.</span>
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: BRAND }} />
                  <span><span className="font-semibold text-gray-800">Usage data</span> — pages visited, actions taken, and device/browser information collected automatically.</span>
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: BRAND }} />
                  <span><span className="font-semibold text-gray-800">Communications</span> — messages you send us through the Contact page or support channels.</span>
                </li>
              </ul>
            </section>

            <section
              id="how-we-use-it"
              ref={(el) => (sectionRefs.current['how-we-use-it'] = el)}
              className="space-y-3 scroll-mt-8"
            >
              <h2 className="text-xl font-bold text-gray-900">3. How We Use Your Information</h2>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: BRAND }} />
                  To process orders and connect you with the correct vendor.
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: BRAND }} />
                  To send order confirmations, delivery updates, and respond to support requests.
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: BRAND }} />
                  To improve the platform, prevent fraud, and keep the marketplace secure.
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: BRAND }} />
                  To send optional updates about Dehleze, which you can opt out of at any time.
                </li>
              </ul>
            </section>

            <section
              id="sharing"
              ref={(el) => (sectionRefs.current['sharing'] = el)}
              className="space-y-3 scroll-mt-8"
            >
              <h2 className="text-xl font-bold text-gray-900">4. Sharing With Vendors & Partners</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                Because Dehleze is a marketplace, some information is necessarily shared to
                fulfill your order:
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: BRAND }} />
                  Vendors receive the order details and delivery information needed to fulfill a purchase.
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: BRAND }} />
                  Delivery partners receive the address and contact number needed to complete delivery.
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: BRAND }} />
                  We do not sell your personal information to third parties for advertising purposes.
                </li>
              </ul>
            </section>

            <section
              id="cookies"
              ref={(el) => (sectionRefs.current['cookies'] = el)}
              className="space-y-3 scroll-mt-8"
            >
              <h2 className="text-xl font-bold text-gray-900">5. Cookies & Tracking</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                We use cookies and similar technologies to keep you signed in, remember your
                cart, and understand how the platform is used. You can control cookies through
                your browser settings, though disabling them may affect some features (like
                staying logged in).
              </p>
            </section>

            <section
              id="security"
              ref={(el) => (sectionRefs.current['security'] = el)}
              className="space-y-3 scroll-mt-8"
            >
              <h2 className="text-xl font-bold text-gray-900">6. Data Security</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                We use industry-standard measures — including encrypted connections and secure
                authentication — to protect your information. No system is completely secure,
                but we work to keep your data safe and to respond quickly if something goes
                wrong.
              </p>
            </section>

            <section
              id="your-rights"
              ref={(el) => (sectionRefs.current['your-rights'] = el)}
              className="space-y-3 scroll-mt-8"
            >
              <h2 className="text-xl font-bold text-gray-900">7. Your Rights & Choices</h2>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: BRAND }} />
                  You can review and update your account information at any time.
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: BRAND }} />
                  You can request a copy of the data we hold about you.
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: BRAND }} />
                  You can request that we delete your account and associated data, subject to any orders we're legally required to keep records of.
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: BRAND }} />
                  You can unsubscribe from marketing emails at any time using the link in those emails.
                </li>
              </ul>
              <p className="text-sm text-gray-600 leading-relaxed">
                To exercise any of these rights, reach out via the contact details below.
              </p>
            </section>

            <section
              id="children"
              ref={(el) => (sectionRefs.current['children'] = el)}
              className="space-y-3 scroll-mt-8"
            >
              <h2 className="text-xl font-bold text-gray-900">8. Children's Privacy</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                Dehleze is not directed at children, and we do not knowingly collect
                information from anyone under the age of 13. If you believe a child has
                provided us with personal information, please contact us so we can remove it.
              </p>
            </section>

            <section
              id="changes"
              ref={(el) => (sectionRefs.current['changes'] = el)}
              className="space-y-3 scroll-mt-8"
            >
              <h2 className="text-xl font-bold text-gray-900">9. Changes to This Policy</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                We may update this Privacy Policy from time to time. If we make significant
                changes, we'll update the "Last updated" date above and, where appropriate,
                notify you directly.
              </p>
            </section>

            <section
              id="contact"
              ref={(el) => (sectionRefs.current['contact'] = el)}
              className="space-y-3 scroll-mt-8"
            >
              <h2 className="text-xl font-bold text-gray-900">10. Contact Us</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                If you have questions about this Privacy Policy or how your data is handled,
                reach out to us:
              </p>
              <div
                className="rounded-2xl p-5 flex flex-col sm:flex-row gap-4 sm:gap-8 border"
                style={{ backgroundImage: 'linear-gradient(135deg, #fff3e0, #fff0f5)', borderColor: '#ffe3b3' }}
              >
                <div>
                  <div className="text-xs text-gray-500">Email</div>
                  <div className="text-sm text-gray-800 font-medium">dehleze.pk@gmail.com</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Phone</div>
                  <div className="text-sm text-gray-800 font-medium">+92 322 4540783</div>
                </div>
              </div>
              <Link
                to="/contact"
                className="inline-block mt-2 rounded-full px-6 py-3 text-sm font-semibold text-white shadow-lg hover:scale-[1.03] transition-transform"
                style={{
                  backgroundImage: `linear-gradient(to right, #ffb74d, ${BRAND})`,
                  boxShadow: '0 10px 25px -8px rgba(253,158,16,0.45)',
                }}
              >
                Go to Contact page
              </Link>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
